import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuthState";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Trophy,
  Menu,
  User,
  LogOut,
  Settings,
  BarChart3,
  Sparkles,
  Brain,
  MapPin, // Import MapPin for Journey Builder
  Bell,
} from "lucide-react";
import prizeDropLogo from "@assets/logo 7_1756706183202.png";
import { apiRequest, queryClient } from "@/lib/queryClient";

export default function Navbar() {
  const [location, setLocation] = useLocation();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isNavbarHidden, setIsNavbarHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxOpen, setInboxOpen] = useState(false);
  const isAuthenticated = !!user;

  const navItems = [
    { href: "/", label: "Home" },
    { href: "/competitions", label: "Competitions" },
    { href: "/winners", label: "Winners" },
    { href: "/testimonials", label: "Testimonials" },
    { href: "/challenges", label: "Challenges" },
    // { href: "/blog", label: "Blog" }, // Hidden temporarily
    { href: "/about", label: "About Us" },
    { href: "/faq", label: "FAQ" },
  ];

  const isActive = (path: string) => location === path;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show navbar when at top of page
      if (currentScrollY <= 0) {
        setIsNavbarHidden(false);
        setLastScrollY(currentScrollY);
        return;
      }

      // Only apply hide/show logic on mobile (screens < md breakpoint, which is 768px)
      if (window.innerWidth < 768) {
        // Scrolling down the page (increasing scroll) - show navbar
        if (currentScrollY > lastScrollY) {
          setIsNavbarHidden(false);
        }
        // Scrolling up the page (decreasing scroll) - hide navbar
        else if (currentScrollY < lastScrollY) {
          setIsNavbarHidden(true);
        }
      } else {
        // Always show on desktop
        setIsNavbarHidden(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  // Poll unread in-app message count when authenticated
  useEffect(() => {
    let interval: number | undefined;

    const fetchUnread = async () => {
      if (!isAuthenticated) {
        setUnreadCount(0);
        return;
      }
      try {
        const response = await apiRequest("GET", "/api/inbox/unread-count");
        const data = await response.json();
        setUnreadCount(data.unreadCount ?? 0);
      } catch (error) {
        console.error("[NAVBAR] Failed to fetch unread inbox count:", error);
      }
    };

    fetchUnread();
    if (isAuthenticated) {
      interval = window.setInterval(fetchUnread, 60000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      // Clear all query cache first
      queryClient.clear();
      // Then sign out (this handles navigation internally)
      await signOut();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <>
      <nav
        className={`bg-white shadow-sm sticky top-0 z-50 transition-transform duration-300 ease-in-out ${
          isNavbarHidden ? "-translate-y-full md:translate-y-0" : "translate-y-0"
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div
          className="flex justify-between items-center py-2"
          style={{ minHeight: "60px" }}
        >
          {/* Logo */}
          <div className="flex items-center">
            <Link href="/">
              <div className="flex items-center cursor-pointer">
                <img
                  src={prizeDropLogo}
                  alt="Prize Drop"
                  className="h-8 w-auto 2xs:h-9 mobile-sm:h-10 mobile-md:h-11 xs:h-12 sm:h-12 md:h-14 lg:h-16 xl:h-18 object-contain max-w-none"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Only show for unauthenticated users */}
          {!isAuthenticated && (
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navItems.map((item) => (
                  <Link key={item.href} href={item.href}>
                    <button
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive(item.href)
                          ? "text-primary bg-primary/10"
                          : "text-gray-700 hover:text-primary hover:bg-gray-50"
                      }`}
                    >
                      <span className="flex items-center">
                        {item.label}
                        {item.label === "Challenges" && (
                          <span className="new-badge">NEW</span>
                        )}
                      </span>
                    </button>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Desktop Auth */}
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6">
              {isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  <Link href="/user">
                    <Button variant="ghost" size="sm">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Dashboard
                    </Button>
                  </Link>

                  <Link href="/competitions">
                    <Button variant="ghost" size="sm">
                      <Trophy className="mr-2 h-4 w-4" />
                      All Competitions
                    </Button>
                  </Link>

                  <Link href="/challenges">
                    <Button variant="ghost" size="sm">
                      <Trophy className="mr-2 h-4 w-4" />
                      <span className="flex items-center">
                        Challenges
                        <span className="new-badge">NEW</span>
                      </span>
                    </Button>
                  </Link>

                  {user?.role === "admin" && (
                    <Link href="/admin">
                      <Button variant="ghost" size="sm">
                        <Settings className="mr-2 h-4 w-4" />
                        Admin
                      </Button>
                    </Link>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.profileImageUrl} />
                          <AvatarFallback>
                            {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-4 rounded-full bg-red-500 px-[3px] text-[10px] font-medium leading-none text-white">
                            {unreadCount > 9 ? "9+" : unreadCount}
                          </span>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-56 !-translate-x-[200px]"
                      align="end"
                    >
                      <div className="flex items-center justify-start gap-2 p-2">
                        <div className="flex flex-col space-y-1 leading-none">
                          <p className="font-medium">
                            {user?.firstName && user?.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user?.email?.split("@")[0]}
                          </p>
                          <p className="w-[200px] truncate text-sm text-muted-foreground">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link href="/profile">
                          <User className="mr-2 h-4 w-4" />
                          Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between"
                          onClick={() => setInboxOpen(true)}
                        >
                          <span className="inline-flex items-center">
                            <Bell className="mr-2 h-4 w-4" />
                            Inbox
                          </span>
                          {unreadCount > 0 && (
                            <span className="inline-flex items-center justify-center h-4 min-w-4 rounded-full bg-red-500 px-[3px] text-[10px] font-medium leading-none text-white">
                              {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                          )}
                        </button>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/winners">
                          <Trophy className="mr-2 h-4 w-4" />
                          Winners
                        </Link>
                      </DropdownMenuItem>

                      {/* Admin section for Journey Builder */}
                      {user?.role === "admin" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link href="/admin/journey-builder">
                              <MapPin className="mr-2 h-4 w-4" />
                              Journey Builder
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}

                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Preserve referral code and redirect URL when navigating to auth
                      const urlParams = new URLSearchParams(
                        window.location.search,
                      );
                      const ref = urlParams.get("ref");
                      const currentPath = window.location.pathname;

                      // Build auth URL with preserved parameters
                      let authUrl = "/auth";
                      const params = new URLSearchParams();

                      if (currentPath.includes("/competition/")) {
                        params.set("redirect", currentPath);
                      }
                      if (ref) {
                        params.set("ref", ref);
                      }

                      if (params.toString()) {
                        authUrl += "?" + params.toString();
                      }

                      setLocation(authUrl);
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    className="primary-gradient"
                    onClick={() => {
                      // Preserve referral code and redirect URL when navigating to auth
                      const urlParams = new URLSearchParams(
                        window.location.search,
                      );
                      const ref = urlParams.get("ref");
                      const currentPath = window.location.pathname;

                      // Build auth URL with preserved parameters
                      let authUrl = "/auth?tab=signup";
                      const params = new URLSearchParams();

                      if (currentPath.includes("/competition/")) {
                        params.set("redirect", currentPath);
                      }
                      if (ref) {
                        params.set("ref", ref);
                      }

                      // Add tab parameter for signup
                      params.set("tab", "signup");

                      authUrl = "/auth?" + params.toString();

                      setLocation(authUrl);
                    }}
                  >
                    Register
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile navigation */}
          <div className="md:hidden">
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                  onClick={() => setInboxOpen(true)}
                  aria-label="Notifications"
                >
                  <Bell className="h-4 w-4 text-gray-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-4 rounded-full bg-red-500 px-[3px] text-[10px] font-medium leading-none text-white">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-[280px] sm:w-[320px] md:w-[400px]"
                  >
                  <div className="flex flex-col space-y-4 mt-6">
                    {/* User Info */}
                    <div className="flex items-center space-x-3 pb-4 border-b">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user?.profileImageUrl} />
                        <AvatarFallback>
                          {user?.firstName?.[0] ||
                            user?.email?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <p className="font-medium text-sm">
                          {user?.firstName && user?.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user?.email?.split("@")[0]}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user?.email}
                        </p>
                      </div>
                    </div>

                    {/* Navigation Links */}
                    <div className="flex flex-col space-y-2">
                      <Link
                        href="/user"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                        >
                          <BarChart3 className="mr-3 h-5 w-5" />
                          Dashboard
                        </Button>
                      </Link>

                      <Link
                        href="/competitions"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                        >
                          <Trophy className="mr-3 h-5 w-5" />
                          All Competitions
                        </Button>
                      </Link>

                      <Link
                        href="/winners"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                        >
                          <Trophy className="mr-3 h-5 w-5" />
                          Winners
                        </Button>
                      </Link>

                      <Link
                        href="/challenges"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                        >
                          <Trophy className="mr-3 h-5 w-5" />
                          <span className="flex items-center">
                            Challenges
                            <span className="new-badge">NEW</span>
                          </span>
                        </Button>
                      </Link>

                      <Link
                        href="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                        >
                          <User className="mr-3 h-5 w-5" />
                          Profile
                        </Button>
                      </Link>

                      {user?.role === "admin" && (
                        <Link
                          href="/admin/journey-builder"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                          >
                            <MapPin className="mr-3 h-5 w-5" />
                            Journey Builder
                          </Button>
                        </Link>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="pt-4 border-t">
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                      >
                        <LogOut className="mr-3 h-5 w-5" />
                        Logout
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              </div>
            ) : (
              // Show Login button for unauthenticated users - opens signup tab
              <Link href="/auth?tab=signup">
                <Button
                  variant={location === "/auth" ? "default" : "ghost"}
                  size="sm"
                  className={
                    location === "/auth"
                      ? "bg-primary text-primary-foreground"
                      : ""
                  }
                >
                  <User className="mr-2 h-4 w-4" />
                  Login
                </Button>
              </Link>
            )}
          </div>
        </div>
        </div>
      </nav>
      {/* Inbox drawer (desktop + mobile) */}
      {isAuthenticated && inboxOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
          <div className="h-full w-full max-w-sm bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <h2 className="text-sm font-semibold">Notifications</h2>
                <p className="text-xs text-muted-foreground">
                  In-app messages and important updates
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setInboxOpen(false)}>
                ✕
              </Button>
            </div>
            <InboxList onClose={() => setInboxOpen(false)} onUnreadChange={setUnreadCount} />
          </div>
        </div>
      )}
    </>
  );
}

interface InboxListProps {
  onClose: () => void;
  onUnreadChange: (count: number) => void;
}

function InboxList({ onClose, onUnreadChange }: InboxListProps) {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<
    { id: number; title: string; body: string; isRead: boolean; messageType?: string; createdAt?: string }[]
  >([]);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("GET", "/api/inbox");
      const data = await response.json();
      const items = data.messages || [];
      setMessages(items);
      const unread = items.filter((m: any) => !m.isRead).length;
      onUnreadChange(unread);
      setLoading(false);
    } catch (error) {
      console.error("[INBOX] Failed to load messages:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const markAsRead = async (ids: number[]) => {
    try {
      if (!ids.length) return;
      await apiRequest("POST", "/api/inbox/mark-read", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const updated = messages.map((m) =>
        ids.includes(m.id) ? { ...m, isRead: true } : m
      );
      setMessages(updated);
      const unread = updated.filter((m) => !m.isRead).length;
      onUnreadChange(unread);
    } catch (error) {
      console.error("[INBOX] Failed to mark messages read:", error);
    }
  };

  const markAllRead = async () => {
    const ids = messages.filter((m) => !m.isRead).map((m) => m.id);
    await markAsRead(ids);
  };

  const deleteMessages = async (ids: number[]) => {
    try {
      if (!ids.length) return;
      await apiRequest("POST", "/api/inbox/delete", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const remaining = messages.filter((m) => !ids.includes(m.id));
      setMessages(remaining);
      const unread = remaining.filter((m) => !m.isRead).length;
      onUnreadChange(unread);
    } catch (error) {
      console.error("[INBOX] Failed to delete messages:", error);
    }
  };

  const clearInbox = async () => {
    try {
      if (!messages.length) return;
      await apiRequest("POST", "/api/inbox/delete", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deleteAll: true }),
      });
      setMessages([]);
      onUnreadChange(0);
    } catch (error) {
      console.error("[INBOX] Failed to clear inbox:", error);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <Button variant="ghost" size="sm" onClick={fetchMessages}>
          Refresh
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={markAllRead}>
            Mark all as read
          </Button>
          <Button variant="ghost" size="sm" onClick={clearInbox}>
            Clear inbox
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted-foreground">No notifications yet.</p>
        ) : (
          messages.map((msg) => {
            const isExpanded = expandedIds.includes(msg.id);
            const isLong = (msg.body || "").length > 220;

            const previewBody =
              !isLong || isExpanded ? msg.body : `${msg.body.slice(0, 220)}…`;

            return (
              <div
                key={msg.id}
                className={`rounded-md border px-3 py-2 text-xs ${
                  msg.isRead ? "bg-white" : "bg-primary/5 border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{msg.title}</span>
                  {msg.createdAt && (
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(msg.createdAt).toLocaleString()}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-700 whitespace-pre-line">{previewBody}</p>
                <div className="mt-1 flex items-center justify-between">
                  {isLong && (
                    <button
                      type="button"
                      className="text-[11px] font-medium text-primary hover:underline"
                      onClick={() => {
                        const nextExpanded = expandedIds.includes(msg.id)
                          ? expandedIds.filter((id) => id !== msg.id)
                          : [...expandedIds, msg.id];
                        setExpandedIds(nextExpanded);
                        if (!msg.isRead) {
                          markAsRead([msg.id]);
                        }
                      }}
                    >
                      {isExpanded ? "Show less" : "Read full message"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-[11px] text-muted-foreground hover:text-red-600"
                    onClick={() => deleteMessages([msg.id])}
                  >
                    Clear
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="px-4 py-2 border-t">
        <Button variant="outline" className="w-full" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
